<template>
  <v-navigation-drawer
    :value="open"
    app
    clipped
    :permanent="device.isDesktop"
    :temporary="!device.isDesktop"
    :mini-variant="rail"
    :width="device.isDesktop ? 264 : 300"
    :mini-variant-width="72"
    class="app-nav"
    :class="{ 'app-nav--rail': rail }"
    @input="onDrawerInput"
  >
    <!-- Telefon i tablet: kartica naloga, jezik. Stoji na vrhu dok se meni skroluje. -->
    <template v-if="!device.isDesktop" #prepend>
      <div class="app-nav__account">
        <v-avatar color="rgba(251, 109, 59, 0.16)" size="44" class="app-nav__avatar">
          <span>{{ initials }}</span>
        </v-avatar>
        <div class="app-nav__account-text">
          <b>{{ toLatin(user.name) }} {{ toLatin(user.lastname || "") }}</b>
          <span>{{ roleLabel }}</span>
        </div>
      </div>
      <language-selector
        v-if="canSeeLanguage && availableLanguages.length > 1"
        component-type="autocomplete"
        dense
        component-class="mx-3 mt-3"
        @setLang="$emit('set-lang', $event)"
      />
    </template>

    <nav class="app-nav__nav" :aria-label="$t('nav_main_menu')" @keydown="onNavKey">
      <label v-if="grouped || !device.isDesktop" v-show="!rail" class="app-nav__filter">
        <v-icon size="18">mdi-magnify</v-icon>
        <input
          ref="filter"
          v-model="query"
          type="text"
          autocomplete="off"
          :placeholder="$t('nav_search_menu')"
          :aria-label="$t('nav_search_menu')"
          @keydown.esc.stop.prevent="onFilterEsc"
        />
        <button
          v-if="query"
          type="button"
          class="app-nav__filter-x"
          :aria-label="$t('close')"
          @click="clearQuery"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </label>

      <div class="app-nav__scroll">
        <!-- Prikačeno ili nedavno -->
        <div v-if="quick.length && !query && !rail" class="app-nav__group app-nav__group--quick">
          <div class="app-nav__cap app-nav__cap--static">
            <v-icon size="12">mdi-star</v-icon>
            <span>{{ navState.pins.length ? $t("nav_pinned") : $t("nav_recent") }}</span>
          </div>
          <nav-item
            v-for="item in quick"
            :key="'q-' + menuKey(item)"
            :item="item"
            :label="menuLabel(item)"
            :pinned="isPinned(item)"
            @pin="onPin"
            @go="onGo"
          />
        </div>

        <div
          v-for="group in visibleGroups"
          :key="group.id"
          class="app-nav__group"
          :class="{ 'app-nav__group--solo': group.solo }"
          :role="group.solo ? null : 'group'"
          :aria-label="grouped && !group.solo ? $t('nav_group_' + group.id) : null"
        >
          <button
            v-if="grouped && !rail && !group.solo"
            type="button"
            class="app-nav__cap"
            data-nav-focus
            :aria-expanded="String(isOpen(group))"
            @click="toggleGroup(group)"
          >
            <v-icon size="14" class="app-nav__chev" :class="{ 'is-closed': !isOpen(group) }">mdi-chevron-down</v-icon>
            <span>{{ $t("nav_group_" + group.id) }}</span>
            <span v-if="!isOpen(group)" class="app-nav__count">{{ group.items.length }}</span>
          </button>
          <template v-if="isOpen(group) || rail">
            <nav-item
              v-for="item in group.items"
              :key="menuKey(item)"
              :item="item"
              :label="menuLabel(item)"
              :rail="rail"
              :query="query"
              :pinned="isPinned(item)"
              @pin="onPin"
              @go="onGo"
            />
          </template>
        </div>

        <div v-if="!visibleGroups.length" class="app-nav__empty">
          <b>{{ $t("nav_no_match", { q: query }) }}</b>
          <span>{{ $t("nav_press_search") }}</span>
        </div>
      </div>
    </nav>

    <template #append>
      <div class="app-nav__foot">
        <button
          v-if="device.isDesktop"
          type="button"
          class="app-nav__foot-btn"
          :aria-label="rail ? $t('nav_expand') : $t('nav_collapse')"
          @click="$emit('toggle-rail')"
        >
          <v-icon size="20">{{ rail ? "mdi-chevron-right" : "mdi-chevron-left" }}</v-icon>
          <span v-if="!rail">{{ $t("nav_collapse") }}</span>
        </button>
        <button v-else type="button" class="app-nav__foot-btn app-nav__foot-btn--out" @click="$emit('logout')">
          <v-icon size="22">mdi-logout</v-icon>
          <span>{{ $t("logout") }}</span>
        </button>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import LanguageSelector from "@/components/layout/LanguageSelector.vue";
import NavItem from "@/components/layout/NavItem.vue";
import navMenu from "@/mixins/navMenu";
import { groupMenu, shouldGroup } from "@/utils/menuGroups";
import { matchesText } from "@/utils/searchText";
import { navState, setGroupOpen, togglePin, pushRecent } from "@/utils/navPrefs";

export default {
  name: "AppNavigationDrawer",
  components: { LanguageSelector, NavItem },
  mixins: [navMenu],
  props: {
    drawer: { type: Boolean, required: true },
    availableLanguages: { type: Array, default: () => [] },
    menu: { type: Array, required: true },
    rail: { type: Boolean, default: false },
  },
  data: () => ({ query: "", navState }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    open() {
      return this.device.isDesktop ? true : this.drawer;
    },
    canSeeLanguage() {
      return ["admin", "restaurant", "admin_delivery", "company", "report_guest"].includes(this.user?.type);
    },
    initials() {
      return [this.user.name, this.user.lastname]
        .filter(Boolean)
        .map((name) => this.toLatin(name.charAt(0).toUpperCase()))
        .join("");
    },
    roleLabel() {
      const key = "nav_role_" + this.user?.type;
      return this.$te(key) ? this.$t(key) : "";
    },
    // Stavke u ravnoj listi: podstavke (child) se otvaraju u isti niz, "Lokacija" samo za prenosive korisnike.
    items() {
      const flat = [];
      this.menu.forEach((item) => {
        if (item.child && item.child.length) {
          item.child.forEach((child) => flat.push({ ...child, icon: child.icon || item.icon, _translate: true }));
        } else {
          flat.push(item);
        }
      });
      return flat.filter((item) => this.menuVisible(item));
    },
    grouped() {
      return shouldGroup(this.items);
    },
    // Grupa s jednom stavkom nema naslov: stavka stoji sama i otvara se jednim klikom.
    groups() {
      if (!this.grouped) return [{ id: "all", items: this.items }];
      return groupMenu(this.items).map((group) => ({ ...group, solo: group.items.length === 1 }));
    },
    visibleGroups() {
      if (!this.query.trim()) return this.groups;
      return this.groups
        .map((g) => ({ ...g, items: g.items.filter((item) => matchesText(this.menuLabel(item), this.query)) }))
        .filter((g) => g.items.length);
    },
    // Prikačene stranice, a bez njih tri zadnje posjećene (ne i trenutna).
    quick() {
      if (!this.grouped) return [];
      const byKey = {};
      this.items.forEach((item) => (byKey[this.menuKey(item)] = item));
      const pinned = navState.pins.map((k) => byKey[k]).filter(Boolean);
      if (pinned.length) return pinned.slice(0, 5);
      return navState.recent
        .map((k) => byKey[k])
        .filter((item) => item && !this.menuItemActive(item))
        .slice(0, 3);
    },
  },
  methods: {
    onDrawerInput(value) {
      if (!this.device.isDesktop) this.$emit("update:drawer", value);
    },
    isPinned(item) {
      return navState.pins.includes(this.menuKey(item));
    },
    onPin(item) {
      togglePin(this.menuKey(item));
    },
    onGo(item) {
      pushRecent(this.menuKey(item));
      if (!this.device.isDesktop) this.$emit("update:drawer", false);
    },
    // Grupa je otvorena: kad pretraga traje, kad u njoj stoji trenutna stranica ili je prva s naslovom; inače po izboru.
    isOpen(group) {
      if (!this.grouped || group.solo || this.query.trim()) return true;
      const saved = navState.collapsed[group.id];
      if (saved !== undefined) return !saved;
      const first = this.groups.find((g) => !g.solo);
      return group.items.some((item) => this.menuItemActive(item)) || (first && group.id === first.id);
    },
    toggleGroup(group) {
      setGroupOpen(group.id, !this.isOpen(group));
    },
    clearQuery() {
      this.query = "";
      this.$nextTick(() => this.$refs.filter && this.$refs.filter.focus());
    },
    onFilterEsc() {
      if (this.query) this.query = "";
      else if (this.$refs.filter) this.$refs.filter.blur();
    },
    // Strelice, Home i End kreću se kroz stavke i naslove grupa.
    onNavKey(event) {
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      const target = event.target.closest && event.target.closest("[data-nav-focus]");
      if (!target) return;
      const list = Array.from(this.$el.querySelectorAll("[data-nav-focus]"));
      const at = list.indexOf(target);
      if (at < 0) return;
      event.preventDefault();
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
          ? list.length - 1
          : Math.max(0, Math.min(list.length - 1, at + (event.key === "ArrowDown" ? 1 : -1)));
      list[next].focus();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/_variables.scss";

.app-nav {
  --nav-ink: #c2410c;
  --nav-text: #3a2f28;
  --nav-muted: #6b5a50;
  --nav-cap: #756559;
  --nav-hover: #f7f1ed;
  --nav-active: #fff1ea;
  --nav-active-ink: #9a3412;
  --nav-line: #f0e2d9;
  --nav-focus: #1d4ed8;

  &.v-navigation-drawer {
    border-right: 1px solid var(--nav-line) !important;
  }
  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
  }
  &__nav {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  &__account {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: #fffaf7;
    border-bottom: 1px solid var(--nav-line);
  }
  &__avatar span {
    color: var(--nav-active-ink);
    font-weight: 700;
  }
  &__account-text {
    display: grid;
    min-width: 0;
    line-height: 1.25;
    b {
      font-size: 0.95rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    span {
      font-size: 0.78rem;
      color: var(--nav-muted);
    }
  }
  &__filter {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 12px 12px 6px;
    padding: 0 6px 0 10px;
    height: 38px;
    border: 1px solid var(--nav-line);
    border-radius: 10px;
    background: #faf6f3;
    color: var(--nav-muted);
    cursor: text;
    &:focus-within {
      border-color: var(--nav-focus);
      background: #fff;
      box-shadow: 0 0 0 2px #dbe6ff;
    }
    input {
      flex: 1;
      min-width: 0;
      height: 100%;
      border: 0;
      outline: 0;
      background: transparent;
      font-size: 0.9rem;
      color: #1c1410;
    }
  }
  &__filter-x {
    width: 26px;
    height: 26px;
    border: 0;
    background: transparent;
    border-radius: 8px;
    display: grid;
    place-items: center;
    color: var(--nav-muted);
    &:hover {
      background: var(--nav-hover);
    }
  }
  &__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 4px 8px 10px;
    min-height: 0;
    scrollbar-width: thin;
  }
  &__cap {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    border: 0;
    background: transparent;
    padding: 12px 10px 4px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--nav-cap);
    text-align: left;
    border-radius: 8px;
    cursor: pointer;
    &:hover:not(.app-nav__cap--static) {
      color: #1c1410;
    }
    &--static {
      cursor: default;
    }
  }
  /* grupa s jednom stavkom: bez naslova, odvojena crtom od onoga iznad; susjedne usamljene dijele jednu crtu */
  &__group--solo {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--nav-line);
    &:first-child {
      margin-top: 0;
      padding-top: 0;
      border-top: 0;
    }
    & + & {
      margin-top: 0;
      padding-top: 0;
      border-top: 0;
    }
  }
  &__chev {
    transition: transform 0.15s;
    &.is-closed {
      transform: rotate(-90deg);
    }
  }
  &__count {
    margin-left: auto;
    background: #f3ece7;
    border-radius: 8px;
    padding: 0 6px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--nav-muted);
  }
  &__empty {
    display: grid;
    gap: 6px;
    justify-items: center;
    text-align: center;
    padding: 28px 12px;
    font-size: 0.86rem;
    color: var(--nav-muted);
    b {
      color: #1c1410;
    }
  }
  &__foot {
    border-top: 1px solid var(--nav-line);
    padding: 8px;
  }
  &__foot-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 40px;
    padding: 0 10px;
    border: 0;
    background: transparent;
    border-radius: 10px;
    color: var(--nav-muted);
    font-weight: 600;
    font-size: 0.86rem;
    cursor: pointer;
    &:hover {
      background: var(--nav-hover);
      color: #1c1410;
    }
    &--out {
      height: 46px;
      color: var(--nav-text);
    }
  }

  /* stavka menija */
  .nav-row {
    position: relative;
    display: flex;
    align-items: center;
  }
  .nav-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    height: 40px;
    padding: 0 10px;
    border-radius: 10px;
    color: var(--nav-text);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    min-width: 0;
    position: relative;
    &:hover {
      background: var(--nav-hover);
    }
    &.is-active {
      background: var(--nav-active);
      color: var(--nav-active-ink);
      font-weight: 700;
      &::before {
        content: "";
        position: absolute;
        left: -8px;
        top: 8px;
        bottom: 8px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: $primary-color;
      }
      .nav-ic {
        color: var(--nav-ink) !important;
      }
    }
    mark {
      background: #ffe3a3;
      color: inherit;
      border-radius: 3px;
      padding: 0 1px;
    }
  }
  .nav-ic {
    color: var(--nav-muted) !important;
    flex: none;
  }
  .nav-lb {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .nav-pin {
    position: absolute;
    right: 4px;
    top: 50%;
    margin-top: -14px;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    border-radius: 8px;
    display: grid;
    place-items: center;
    color: var(--nav-muted);
    opacity: 0;
    cursor: pointer;
    &:hover {
      background: #fff;
      box-shadow: 0 0 0 1px var(--nav-line);
    }
  }
  .nav-row:hover .nav-pin,
  .nav-row:focus-within .nav-pin,
  .nav-row.is-pinned .nav-pin {
    opacity: 1;
  }
  .nav-row.is-pinned .nav-pin {
    color: var(--nav-ink);
  }
  .nav-item:focus-visible,
  .nav-pin:focus-visible,
  .app-nav__cap:focus-visible,
  .app-nav__foot-btn:focus-visible,
  .app-nav__filter-x:focus-visible {
    outline: 2px solid var(--nav-focus);
    outline-offset: 1px;
  }

  /* šine */
  &--rail {
    .app-nav__scroll {
      padding: 8px 0 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    .app-nav__group {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 6px 0;
      border-top: 1px solid var(--nav-line);
      &:first-child {
        border-top: 0;
      }
    }
    .app-nav__group--solo {
      margin-top: 0;
      padding-top: 6px;
      border-top: 1px solid var(--nav-line);
      &:first-child,
      & + .app-nav__group--solo {
        padding-top: 0;
        border-top: 0;
      }
    }
    .nav-row {
      width: 48px;
    }
    .nav-item {
      justify-content: center;
      padding: 0;
      width: 48px;
      height: 44px;
      &.is-active::before {
        left: -12px;
      }
    }
    .app-nav__foot-btn {
      justify-content: center;
      padding: 0;
    }
  }
}

.app-nav-tip {
  font-weight: 600;
  font-size: 0.78rem !important;
  background: #1c1410 !important;
  opacity: 1 !important;
}

@media (max-width: 1023px) {
  .app-nav {
    max-width: 88vw;
    .nav-item {
      height: 44px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-nav,
  .app-nav * {
    transition: none !important;
  }
}
</style>
